export const LEAFLET_NO_ANIMATE = { animate: false } as const;

export type LeafletViewportMap = {
  getContainer: () => { isConnected?: boolean; parentNode?: Node | null };
  getPane: (name: string) => unknown;
  stop?: () => void;
  fitBounds: (
    bounds: [number, number][],
    options?: { padding?: [number, number]; maxZoom?: number; animate?: boolean },
  ) => void;
  setView: (
    center: [number, number],
    zoom: number,
    options?: { animate?: boolean },
  ) => void;
};

export function isLeafletMapUsable(
  map: LeafletViewportMap | null | undefined,
): map is LeafletViewportMap {
  if (!map) return false;
  try {
    const container = map.getContainer();
    if (!container?.isConnected && !container?.parentNode) return false;
    return Boolean(map.getPane('mapPane'));
  } catch {
    return false;
  }
}

/** Leaflet `stop()` can throw `_leaflet_pos` once panes are already gone (React 19 unmount). */
export function stopLeafletMapSafely(map: LeafletViewportMap | null | undefined): void {
  if (!isLeafletMapUsable(map)) return;
  try {
    map.stop?.();
  } catch {
    // Map is mid-teardown; ignore.
  }
}

export function syncLeafletViewport(
  map: LeafletViewportMap,
  markerPositions: [number, number][],
  fallbackCenter: [number, number],
  fallbackZoom: number,
  singleMarkerZoom: number,
): boolean {
  if (!isLeafletMapUsable(map)) return false;
  stopLeafletMapSafely(map);
  if (markerPositions.length > 1) {
    map.fitBounds(markerPositions, { padding: [48, 48], maxZoom: 14, ...LEAFLET_NO_ANIMATE });
    return true;
  }
  if (markerPositions.length === 1) {
    map.setView(markerPositions[0], singleMarkerZoom, LEAFLET_NO_ANIMATE);
    return true;
  }
  map.setView(fallbackCenter, fallbackZoom, LEAFLET_NO_ANIMATE);
  return true;
}
